import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useDebounce } from '../hooks/useDebounce';
import { 
  Bell, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Info,
  AlertCircle,
  CheckCircle2,
  Filter,
  Calendar,
  User,
  RefreshCw,
  MessageSquare,
  Trash,
  CheckSquare,
  Square
} from 'lucide-react';

interface Notification {
  _id: string;
  type: 'new_ticket' | 'new_message' | 'ticket_status_changed' | 'admin_broadcast' | 'system_announcement';
  title: string;
  message: string;
  ticketId?: string;
  messageId?: string;
  priority?: string;
  category?: string;
  isRead: boolean;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  userId?: {
    _id: string;
    username: string;
    email: string;
    mysqlUserId?: number;
    name?: string;
    cmsSlug?: string;
  };
  mysqlUserId?: number;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const Notifications: React.FC = () => {
  const { isAdmin } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    per_page: 15,
    total: 0,
    total_pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<boolean | null>(null);
  const [userFilter, setUserFilter] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  
  // Estados para seleção múltipla
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'admin_broadcast' as 'new_ticket' | 'new_message' | 'ticket_status_changed' | 'admin_broadcast' | 'system_announcement',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    target_audience: 'all' as 'all' | 'resellers' | 'masters' | 'admins',
    expires_at: ''
  });

  const notificationTypes = [
    { value: 'new_ticket', label: 'Novo Ticket', icon: MessageSquare, color: 'bg-blue-100 text-blue-800' },
    { value: 'new_message', label: 'Nova Mensagem', icon: MessageSquare, color: 'bg-green-100 text-green-800' },
    { value: 'ticket_status_changed', label: 'Status Alterado', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'admin_broadcast', label: 'Broadcast Admin', icon: Bell, color: 'bg-red-100 text-red-800' },
    { value: 'system_announcement', label: 'Anúncio Sistema', icon: Info, color: 'bg-purple-100 text-purple-800' }
  ];

  const priorities = [
    { value: 'low', label: 'Baixa', color: 'bg-gray-100 text-gray-800' },
    { value: 'medium', label: 'Média', color: 'bg-blue-100 text-blue-800' },
    { value: 'high', label: 'Alta', color: 'bg-orange-100 text-orange-800' },
    { value: 'urgent', label: 'Urgente', color: 'bg-red-100 text-red-800' }
  ];


  const fetchNotifications = useCallback(async (customParams?: any) => {
    if (!isAdmin() || isLoading) return;
    
    // Cache de 30 segundos apenas para chamadas automáticas (sem customParams)
    const now = Date.now();
    if (!customParams && now - lastFetchTime < 30000) {
      return;
    }
    
    setIsLoading(true);
    setLastFetchTime(now);
    try {
      const params = customParams || {
        page: pagination.current_page,
        per: pagination.per_page,
        search: debouncedSearchTerm || undefined,
        type: typeFilter || undefined,
        priority: priorityFilter || undefined,
        isRead: statusFilter !== null ? statusFilter : undefined,
        userSearch: userFilter || undefined
      };

      const response = await apiService.getNotificationsAdmin(params);
      
      // Ajustar para a estrutura real da resposta da API
      setNotifications(response.data || []);
      
      // Se não houver paginação na resposta, usar valores padrão
      if (response.pagination) {
        // Se temos customParams (mudança manual de página), preservar o current_page que foi definido
        if (customParams) {
          const newPagination = {
            ...response.pagination,
            current_page: customParams.page || pagination.current_page
          };
          setPagination(newPagination);
        } else {
          setPagination(response.pagination);
        }
      } else {
        setPagination({
          current_page: customParams?.page || 1,
          per_page: customParams?.per || 15,
          total: (response.data || []).length,
          total_pages: 1
        });
      }
      
      setIsInitialized(true);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      setNotifications([]);
      setPagination({
        current_page: 1,
        per_page: 15,
        total: 0,
        total_pages: 0
      });
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, isLoading, lastFetchTime, userFilter, pagination.current_page, pagination.per_page, debouncedSearchTerm, typeFilter, priorityFilter, statusFilter]);

  // Carregamento inicial apenas uma vez
  useEffect(() => {
    if (isAdmin() && !isInitialized) {
      fetchNotifications();
    }
  }, [isAdmin, isInitialized, fetchNotifications]);

  // Debounce para filtros (só executa se já foi inicializado)
  useEffect(() => {
    if (isInitialized) {
      setPagination(prev => ({ ...prev, current_page: 1 }));
      fetchNotifications();
    }
  }, [debouncedSearchTerm, typeFilter, priorityFilter, statusFilter, isInitialized, fetchNotifications]);

  // Atualizar quando mudar página
  useEffect(() => {
    if (isInitialized) {
      setTimeout(() => {
        fetchNotifications();
      }, 100);
    }
  }, [pagination.current_page, isInitialized, fetchNotifications]);

  const handleSaveNotification = async () => {
    setIsSaving(true);
    try {
      await apiService.createNotificationAdmin({
        type: formData.type,
        title: formData.title,
        message: formData.content,
        priority: formData.priority,
        expiresAt: formData.expires_at || undefined,
        targetUserId: undefined // Sempre para todos os usuários
      });
      
      setShowModal(false);
      setEditingNotification(null);
      resetForm();
      setLastFetchTime(0); // Força nova busca
      fetchNotifications();
    } catch (error) {
      console.error('Erro ao salvar notificação:', error);
      alert('Erro ao salvar notificação');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditNotification = (notification: Notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification.title,
      content: notification.message,
      type: notification.type,
      priority: (notification.priority as 'low' | 'medium' | 'high' | 'urgent') || 'medium',
      target_audience: 'all', // Notificações são sempre para todos por padrão
      expires_at: notification.expiresAt ? new Date(notification.expiresAt).toISOString().slice(0, 16) : ''
    });
    setShowModal(true);
  };

  const handleDeleteNotification = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta notificação?')) {
      try {
        await apiService.deleteNotificationAdmin(id);
        setLastFetchTime(0); // Força nova busca
        fetchNotifications();
      } catch (error) {
        console.error('Erro ao deletar notificação:', error);
        alert('Erro ao deletar notificação');
      }
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await apiService.markNotificationAsRead(id);
      setLastFetchTime(0); // Força nova busca
      fetchNotifications();
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
      alert('Erro ao marcar como lida');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      type: 'admin_broadcast',
      priority: 'medium',
      target_audience: 'all',
      expires_at: ''
    });
  };

  // Funções para seleção múltipla
  const handleSelectNotification = (id: string) => {
    setSelectedNotifications(prev => {
      const newSelected = prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id];
      
      setSelectAll(newSelected.length === notifications.length);
      setShowBulkActions(newSelected.length > 0);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications([]);
      setSelectAll(false);
    } else {
      setSelectedNotifications(notifications.map(n => n._id));
      setSelectAll(true);
    }
    setShowBulkActions(!selectAll);
  };

  const handleBulkDelete = async () => {
    if (selectedNotifications.length === 0) return;

    try {
      const result = await apiService.deleteNotificationsBulk({
        notificationIds: selectedNotifications
      });
      
      alert(`${result.deletedCount} notificação(ões) removida(s) com sucesso`);
      setSelectedNotifications([]);
      setSelectAll(false);
      setShowBulkActions(false);
      setShowBulkDeleteModal(false);
      setLastFetchTime(0); // Força nova busca
      fetchNotifications();
    } catch (error) {
      console.error('Erro ao deletar notificações em massa:', error);
      alert('Erro ao deletar notificações');
    }
  };

  const handleBulkDeleteByFilters = async () => {
    const filters: any = {};
    
    if (typeFilter) filters.type = typeFilter;
    if (priorityFilter) filters.priority = priorityFilter;
    if (statusFilter !== null) filters.isRead = statusFilter;

    try {
      const result = await apiService.deleteNotificationsBulk({ filters });
      
      alert(`${result.deletedCount} notificação(ões) removida(s) com sucesso`);
      setSelectedNotifications([]);
      setSelectAll(false);
      setShowBulkActions(false);
      setShowBulkDeleteModal(false);
      setLastFetchTime(0); // Força nova busca
      fetchNotifications();
    } catch (error) {
      console.error('Erro ao deletar notificações por filtros:', error);
      alert('Erro ao deletar notificações');
    }
  };

  const handleOpenModal = () => {
    setEditingNotification(null);
    resetForm();
    setShowModal(true);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'new_ticket': return <MessageSquare className="h-4 w-4 text-blue-600" />;
      case 'new_message': return <MessageSquare className="h-4 w-4 text-green-600" />;
      case 'ticket_status_changed': return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'admin_broadcast': return <Bell className="h-4 w-4 text-red-600" />;
      case 'system_announcement': return <Info className="h-4 w-4 text-purple-600" />;
      default: return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPriorityBadge = (priority: string) => {
    const classes = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    };
    return classes[priority as keyof typeof classes] || classes.medium;
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-gray-100 text-gray-800';
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Acesso Negado</h2>
          <p className="text-gray-500">Acesso negado. Apenas administradores podem gerenciar notificações.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Notificações</h1>
          <p className="text-gray-600">Crie e gerencie notificações em tempo real para o sistema</p>
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-800">
                <strong>Notificações em Tempo Real:</strong> Avisos urgentes ou importantes que aparecem no sino do header.
                Use para alertas críticos, manutenções e comunicações urgentes.
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => {
              setLastFetchTime(0); // Força nova busca
              fetchNotifications();
            }}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Atualizar
          </Button>
          <Button onClick={handleOpenModal} className="flex items-center gap-2">
            <Plus size={16} />
            Nova Notificação
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Título ou conteúdo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os tipos</option>
                {notificationTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prioridade</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todas as prioridades</option>
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter === null ? '' : statusFilter.toString()}
                onChange={(e) => setStatusFilter(e.target.value === '' ? null : e.target.value === 'true')}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar por Usuário</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Username, email ou ID..."
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Notificações */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Notificações ({pagination.total})</CardTitle>
            {showBulkActions && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {selectedNotifications.length} selecionada(s)
                </span>
                <Button
                  onClick={() => setShowBulkDeleteModal(true)}
                  variant="destructive"
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Trash size={14} />
                  Deletar Selecionadas
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Carregando...</span>
            </div>
          ) : notifications.length > 0 ? (
            <div className="space-y-4">
              {/* Header com seleção geral */}
              <div className="flex items-center gap-3 p-2 border-b border-gray-200">
                <button
                  onClick={handleSelectAll}
                  className="flex-shrink-0 p-1 hover:bg-gray-100 rounded"
                >
                  {selectAll ? (
                    <CheckSquare className="h-5 w-5 text-blue-600" />
                  ) : (
                    <Square className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                <span className="text-sm text-gray-600 font-medium">
                  Selecionar todas
                </span>
              </div>
              
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors ${
                    selectedNotifications.includes(notification._id) ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <button
                        onClick={() => handleSelectNotification(notification._id)}
                        className="flex-shrink-0 mt-1 p-1 hover:bg-gray-100 rounded"
                      >
                        {selectedNotifications.includes(notification._id) ? (
                          <CheckSquare className="h-5 w-5 text-blue-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      
                      <div className="flex-shrink-0 mt-1">
                        {getTypeIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{notification.title}</h4>
                          <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(notification.priority || 'medium')}`}>
                            {priorities.find(p => p.value === notification.priority)?.label || 'Média'}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(!notification.isRead)}`}>
                            {!notification.isRead ? 'Não Lida' : 'Lida'}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">{notification.message}</p>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {notification.userId ? (
                              <span className="flex flex-col">
                                <span className="font-medium">
                                  {notification.userId.name || notification.userId.username}
                                </span>
                                <span className="text-gray-400 text-xs">
                                  {notification.userId.email}
                                  {notification.userId.mysqlUserId && (
                                    <span className="ml-2">[ID: {notification.userId.mysqlUserId}]</span>
                                  )}
                                  {notification.userId.cmsSlug && (
                                    <span className="ml-2 bg-blue-100 text-blue-800 px-1 rounded text-xs">
                                      {notification.userId.cmsSlug}
                                    </span>
                                  )}
                                </span>
                              </span>
                            ) : notification.ticketId ? (
                              `Ticket: ${notification.ticketId}`
                            ) : (
                              'Sistema'
                            )}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(notification.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                          {notification.expiresAt && (
                            <span className="flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Expira: {new Date(notification.expiresAt).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      {!notification.isRead && (
                        <Button
                          onClick={() => handleMarkAsRead(notification._id)}
                          size="sm"
                          variant="outline"
                          className="flex items-center gap-1"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Marcar como Lida
                        </Button>
                      )}
                      <Button
                        onClick={() => handleEditNotification(notification)}
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1"
                      >
                        <Edit className="h-3 w-3" />
                        Editar
                      </Button>
                      <Button
                        onClick={() => handleDeleteNotification(notification._id)}
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhuma notificação encontrada</p>
            </div>
          )}

          {/* Paginação */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-gray-600">
                Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de{' '}
                {pagination.total} notificações
              </p>
              
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    const newPage = pagination.current_page - 1;
                    setPagination(prev => ({ ...prev, current_page: newPage }));
                    fetchNotifications({
                      page: newPage,
                      per: pagination.per_page,
                      search: debouncedSearchTerm || undefined,
                      type: typeFilter || undefined,
                      priority: priorityFilter || undefined,
                      isRead: statusFilter !== null ? statusFilter : undefined,
                      userSearch: userFilter || undefined
                    });
                  }}
                  disabled={pagination.current_page === 1}
                  variant="outline"
                  size="sm"
                >
                  Anterior
                </Button>
                
                <span className="text-sm text-gray-600">
                  Página {pagination.current_page} de {pagination.total_pages}
                </span>
                
                <Button
                  onClick={() => {
                    const newPage = pagination.current_page + 1;
                    setPagination(prev => ({ ...prev, current_page: newPage }));
                    fetchNotifications({
                      page: newPage,
                      per: pagination.per_page,
                      search: debouncedSearchTerm || undefined,
                      type: typeFilter || undefined,
                      priority: priorityFilter || undefined,
                      isRead: statusFilter !== null ? statusFilter : undefined,
                      userSearch: userFilter || undefined
                    });
                  }}
                  disabled={pagination.current_page === pagination.total_pages}
                  variant="outline"
                  size="sm"
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingNotification ? 'Editar Notificação' : 'Nova Notificação'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título da notificação"
                />
              </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mensagem *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Mensagem da notificação"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={4}
                  />
                </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {notificationTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prioridade *</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expira em</label>
                <Input
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <Button
                onClick={() => setShowModal(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveNotification}
                disabled={!formData.title || !formData.content || isSaving}
                className="flex items-center gap-2"
              >
                {isSaving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {isSaving ? 'Salvando...' : editingNotification ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação para Remoção em Massa */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Confirmar Remoção
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja remover <strong>{selectedNotifications.length}</strong> notificação(ões) selecionada(s)?
              <br />
              <span className="text-sm text-red-600 mt-2 block">
                Esta ação não pode ser desfeita.
              </span>
            </p>

            <div className="flex items-center justify-end gap-3">
              <Button
                onClick={() => setShowBulkDeleteModal(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleBulkDelete}
                variant="destructive"
                className="flex items-center gap-2"
              >
                <Trash className="h-4 w-4" />
                Remover {selectedNotifications.length}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Botão para Remoção por Filtros */}
      <div className="mt-4 flex justify-end">
        <Button
          onClick={handleBulkDeleteByFilters}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
          disabled={!typeFilter && !priorityFilter && statusFilter === null}
        >
          <Trash className="h-4 w-4" />
          Deletar por Filtros
        </Button>
      </div>
    </div>
  );
};

export default Notifications;